import { parseCalendarDate } from './calendar-date.js';
import { BUSINESS_TIME_ZONE } from './constants.js';
import { Temporal, toInstant, toPlainDate } from './temporal.js';
const IANA_NAME_SHAPE = /^(UTC|[A-Z][A-Za-z0-9_+-]*(\/[A-Z][A-Za-z0-9_+-]*)+)$/;
const WALL_TIME_SHAPE = /^([01]\d|2[0-3]):[0-5]\d$/;
/** The zone covering most of each state; a state spanning two zones is editable per event. */
export const US_STATE_TIME_ZONES = {
    AK: 'America/Anchorage',
    AL: 'America/Chicago',
    AR: 'America/Chicago',
    AZ: 'America/Phoenix',
    CA: 'America/Los_Angeles',
    CO: 'America/Denver',
    CT: 'America/New_York',
    DC: 'America/New_York',
    DE: 'America/New_York',
    FL: 'America/New_York',
    GA: 'America/New_York',
    HI: 'Pacific/Honolulu',
    IA: 'America/Chicago',
    ID: 'America/Boise',
    IL: 'America/Chicago',
    IN: 'America/Indiana/Indianapolis',
    KS: 'America/Chicago',
    KY: 'America/New_York',
    LA: 'America/Chicago',
    MA: 'America/New_York',
    MD: 'America/New_York',
    ME: 'America/New_York',
    MI: 'America/Detroit',
    MN: 'America/Chicago',
    MO: 'America/Chicago',
    MS: 'America/Chicago',
    MT: 'America/Denver',
    NC: 'America/New_York',
    ND: 'America/Chicago',
    NE: 'America/Chicago',
    NH: 'America/New_York',
    NJ: 'America/New_York',
    NM: 'America/Denver',
    NV: 'America/Los_Angeles',
    NY: 'America/New_York',
    OH: 'America/New_York',
    OK: 'America/Chicago',
    OR: 'America/Los_Angeles',
    PA: 'America/New_York',
    RI: 'America/New_York',
    SC: 'America/New_York',
    SD: 'America/Chicago',
    TN: 'America/Chicago',
    TX: 'America/Chicago',
    UT: 'America/Denver',
    VA: 'America/New_York',
    VT: 'America/New_York',
    WA: 'America/Los_Angeles',
    WI: 'America/Chicago',
    WV: 'America/New_York',
    WY: 'America/Denver',
};
/** `HH:mm` on the 24-hour clock: the wall time a kind-2 field is entered in. */
export function isWallTime(value) {
    return typeof value === 'string' && WALL_TIME_SHAPE.test(value);
}
/** Intl accepts aliases and offsets (`+05:00`), so the shape check keeps stored values IANA names. */
export function isValidTimeZone(value) {
    if (!IANA_NAME_SHAPE.test(value))
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
function assertTimeZone(zone) {
    if (!isValidTimeZone(zone)) {
        throw new RangeError(`Not an IANA time zone: ${JSON.stringify(zone)}`);
    }
}
/** The one state-to-zone map; an unknown or missing state falls back to `BUSINESS_TIME_ZONE`. */
export function defaultTimeZoneForState(stateCode) {
    const code = stateCode ?? '';
    return Object.hasOwn(US_STATE_TIME_ZONES, code) ? US_STATE_TIME_ZONES[code] : BUSINESS_TIME_ZONE;
}
/** A wall time skipped by spring-forward moves an hour later; a repeated one takes the earlier instant. */
export function placeWallTimeToInstant(ymd, hhmm, zone) {
    if (!WALL_TIME_SHAPE.test(hhmm)) {
        throw new RangeError(`Not an HH:mm wall time: ${JSON.stringify(hhmm)}`);
    }
    assertTimeZone(zone);
    const { year, month, day } = toPlainDate(ymd);
    const { hour, minute } = Temporal.PlainTime.from(hhmm);
    const zoned = Temporal.ZonedDateTime.from({ year, month, day, hour, minute, timeZone: zone }, { disambiguation: 'compatible' });
    return new Date(zoned.epochMilliseconds);
}
export function instantToPlaceWallTime(instant, zone) {
    assertTimeZone(zone);
    const zoned = toInstant(instant).toZonedDateTimeISO(zone);
    return {
        date: parseCalendarDate(zoned.toPlainDate().toString()),
        time: zoned.toPlainTime().toString({ smallestUnit: 'minute' }),
    };
}
/**
 * "9:05 AM" (or "9 AM" without minutes): an `HH:mm` wall time on the 12-hour clock, with no zone
 * label, since the wall it reads on is the caller's (a picker's browser, a chart's Pacific axis).
 */
export function formatWallTime(hhmm, { withMinutes = true } = {}) {
    if (!WALL_TIME_SHAPE.test(hhmm)) {
        throw new RangeError(`Not an HH:mm wall time: ${JSON.stringify(hhmm)}`);
    }
    const [hours, minutes] = hhmm.split(':');
    const hour = Number(hours);
    const period = hour < 12 ? 'AM' : 'PM';
    const clockHour = hour % 12 || 12;
    return withMinutes ? `${clockHour}:${minutes} ${period}` : `${clockHour} ${period}`;
}
/**
 * The only string-to-`Date` for instants: the string must carry `Z` or an offset. Any RFC 9557 /
 * ISO 8601 instant Temporal reads is taken (a space for `T`, the basic `20261002T050000Z` form,
 * hour-only times, a `[zone]` annotation); digits past milliseconds are dropped. A shape check
 * stricter than Temporal's would refuse valid server strings for no zone safety gained.
 */
export function parseInstant(iso) {
    try {
        return new Date(Temporal.Instant.from(iso).epochMilliseconds);
    }
    catch (error) {
        throw new RangeError(`Not an ISO instant with an offset: ${JSON.stringify(iso)}`, {
            cause: error,
        });
    }
}
// WHY: GraphQL `DateTime` fields may be null or, from older rows, unparseable; a render must
// never throw on one, so display and list code reads them with this lenient variant.
/** `parseInstant`, but null for an empty value or anything that is not an instant with an offset. */
export function parseInstantOrNull(iso) {
    if (!iso)
        return null;
    try {
        return parseInstant(iso);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=place-time.js.map